import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterModule,
} from '@angular/router';
import { BannerComponent } from '../../../../shared/components/banner/banner.component';
import { CardStructureComponent } from '../../../../shared/components/card-structure/card-structure.component';
import { StructureSkeletonComponent } from '../../../../shared/components/structure-skeleton/structure-skeleton.component';
import { SearchAndFiltrerComponent } from '../../../../shared/components/search-and-filtrer/search-and-filtrer.component';
import { LocationAlertComponent } from '../../../../shared/components/location-alert/location-alert.component';
import {
  LocationService,
  LocationCoordinates,
} from '../../../../core/services/location.service';
import { ReferentielService } from '../../../../core/services/referentiel.service';
import { environment } from '../../../../../environments';
import { CountryStorageService } from '../../../../core/services/country.storage.service';
import { Country } from '../../../../core/interfaces/doctor.interface';
import { Subject, takeUntil } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

interface MedicalStructure {
  id: number;
  name: string;
  address?: string;
  phone?: string;
}

interface StructuresResponse {
  results: MedicalStructure[];
  pagination: {
    totalItems: number;
    pageCount: number;
    currentPage: number;
    pageSize: number;
  };
}

@Component({
  selector: 'app-list-structures',
  standalone: true,
  templateUrl: './list-structures.component.html',
  styleUrls: ['./list-structures.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    BannerComponent,
    CardStructureComponent,
    StructureSkeletonComponent,
    SearchAndFiltrerComponent,
    LocationAlertComponent,
    RouterModule,
  ],
})
export class ListStructuresComponent implements OnInit {
  currentSection: 'medecins' | 'structures' | 'pharmacies' = 'medecins';
  currentTheme = environment.theme.default;
  bannerLinks = [
    { label: 'Portail Public', href: '/' },
    { label: 'Structures de santé', href: undefined },
  ];

  structures: MedicalStructure[] = [];
  loading = false;
  error: string | null = null;

  // Pagination
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;

  // Recherche
  searchQuery = '';
  searchTownId = '';
  searchCountryId: any;
  searchSpeciality = '';
  selectedLanguage = '';
  selectedService = '';
  selectedInsurer = '';

  latitude: number | null = null;
  longitude: number | null = null;

  selectedCountry: Country | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private referentielService: ReferentielService,
    private route: ActivatedRoute,
    private router: Router,
    private countryStorage: CountryStorageService,
    private locationService: LocationService
  ) {
    // Detect route changes to update currentSection
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (this.router.url.startsWith('/structures')) {
          this.currentSection = 'structures';
        } else if (this.router.url.startsWith('/pharmacies')) {
          this.currentSection = 'pharmacies';
        } else {
          this.currentSection = 'medecins';
        }
      }
    });
  }

  ngOnInit(): void {
    this.countryStorage.country$
      .pipe(takeUntil(this.destroy$))
      .subscribe((country) => {
        this.selectedCountry = country;
        if (country) {
          this.searchCountryId = country.id;
        }
      });

    const defaultCountry = localStorage.getItem('defaultCountry');
    if (defaultCountry) {
      try {
        const countryObj = JSON.parse(defaultCountry);
        this.searchCountryId = countryObj.id;
      } catch {
        this.searchCountryId = null;
      }
    }

    // S'abonner aux changements de localisation en temps réel
    // Debounce de 2 secondes pour éviter de recharger trop souvent
    this.locationService.location$
      .pipe(
        debounceTime(2000),
        distinctUntilChanged((prev, curr) => {
          // Ne recharger que si la position a changé significativement (plus de 100m)
          if (!prev || !curr) return prev === curr;
          const distance = this.calculateDistance(
            prev.latitude,
            prev.longitude,
            curr.latitude,
            curr.longitude
          );
          return distance < 0.1; // 100 mètres
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((location) => {
        if (location) {
          this.latitude = location.latitude;
          this.longitude = location.longitude;
          // Recharger les structures avec la nouvelle position
          this.loadStructures();
        } else {
          this.latitude = null;
          this.longitude = null;
        }
      });

    // Charger la position initiale si disponible
    this.loadLocationFromStorage();
    this.loadUrlParams();
    this.loadStructures();
  }

  onLocationActivated(coordinates: LocationCoordinates): void {
    this.latitude = coordinates.latitude;
    this.longitude = coordinates.longitude;
    // Reload structures with location
    this.loadStructures();
  }

  loadLocationFromStorage(): void {
    const location = this.locationService.loadLocationFromStorage();
    if (location) {
      this.latitude = location.latitude;
      this.longitude = location.longitude;
    }
  }

  private loadUrlParams(): void {
    this.route.queryParams.subscribe((params) => {
      const newSearchQuery = params['query'] || '';
      const newSearchTownId = params['townId'] || '';
      const newSearchSpeciality = params['specialisationIds'] || '';
      const newSelectedLanguage = params['language'] || '';
      const newSelectedService = params['service'] || '';
      const newSelectedInsurer = params['insurer'] || '';

      // Vérifier si les paramètres ont changé
      const hasParamsChanged =
        this.searchQuery !== newSearchQuery ||
        this.searchTownId !== newSearchTownId ||
        this.searchSpeciality !== newSearchSpeciality ||
        this.selectedLanguage !== newSelectedLanguage ||
        this.selectedService !== newSelectedService ||
        this.selectedInsurer !== newSelectedInsurer;

      // Update values
      this.searchQuery = newSearchQuery;
      this.searchTownId = newSearchTownId;
      this.searchSpeciality = newSearchSpeciality;
      this.selectedLanguage = newSelectedLanguage;
      this.selectedService = newSelectedService;
      this.selectedInsurer = newSelectedInsurer;

      if (hasParamsChanged) {
        this.currentPage = 1;
        this.loadStructures();
      } else {
        this.currentPage = parseInt(params['page']) || 1;
      }
    });
  }

  loadStructures(): void {
    this.loading = true;
    this.error = null;

    const searchParams = {
      limit: this.pageSize,
      page: this.currentPage,
      query: this.searchQuery || undefined,
      townId: this.searchTownId ? Number(this.searchTownId) : undefined,
      countryId: this.searchCountryId ? this.searchCountryId : undefined,
      specialisationIds: this.searchSpeciality || undefined,
      languageIds: this.selectedLanguage || undefined,
      serviceAvailableIds: this.selectedService || undefined,
      insurerIds: this.selectedInsurer || undefined,
      latitude: this.latitude || undefined,
      longitude: this.longitude || undefined,
    };

    this.referentielService.getMedicalStructures(searchParams).subscribe({
      next: (response: StructuresResponse) => {
        this.structures = response.results;
        this.totalItems = response.pagination.totalItems;
        this.totalPages = response.pagination.pageCount;
        this.currentPage = response.pagination.currentPage;
        this.loading = false;
      },
      error: (error) => {
        console.error(
          'Erreur lors du chargement des structures médicales:',
          error
        );
        this.error = 'Erreur lors du chargement des structures médicales';
        this.loading = false;
      },
    });
  }

  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updateUrlParams();
      this.loadStructures();
    }
  }

  onSearch(): void {
    this.currentPage = 1;
    this.updateUrlParams();
    this.loadStructures();
  }

  private updateUrlParams(): void {
    const queryParams: any = {
      page: this.currentPage > 1 ? this.currentPage : undefined,
    };

    if (this.searchQuery) {
      queryParams.query = this.searchQuery;
    }
    if (this.searchTownId) {
      queryParams.town = this.searchTownId;
    }
    if (this.selectedLanguage) {
      queryParams.language = this.selectedLanguage;
    }
    if (this.selectedService) {
      queryParams.service = this.selectedService;
    }
    if (this.selectedInsurer) {
      queryParams.insurer = this.selectedInsurer;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisiblePages = 5;
    if (this.totalPages <= maxVisiblePages) {
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      let start = Math.max(
        1,
        this.currentPage - Math.floor(maxVisiblePages / 2)
      );
      let end = Math.min(this.totalPages, start + maxVisiblePages - 1);
      if (end - start + 1 < maxVisiblePages) {
        start = Math.max(1, end - maxVisiblePages + 1);
      }
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }
    return pages;
  }

  /**
   * Calculer la distance entre deux points en kilomètres (formule de Haversine)
   */
  private calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const R = 6371; // Rayon de la Terre en km
    const dLat = this.deg2rad(lat2 - lat1);
    const dLon = this.deg2rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.deg2rad(lat1)) *
        Math.cos(this.deg2rad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private deg2rad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
